import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { MessageSquareText, Save, RotateCcw, History, Loader2, X, Check, FileText, Link2, Send } from "lucide-react";
import { toast } from "sonner";
import { AdminLinks } from "./AdminLinks";
import { AdminBroadcast } from "./AdminBroadcast";

const varChips = (req = []) => req.map((v) => `{${v}}`);

export default function AdminMessages() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [sms, setSms] = useState("");
  const [wa, setWa] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState(null);
  const [tab, setTab] = useState("templates");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await endpoints.msgTemplates();
      setTemplates(data.templates || []);
    } catch { toast.error("Failed to load templates"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openEdit = (t) => {
    setSel(t); setSms(t.sms || ""); setWa(t.whatsapp || ""); setActive(t.active !== false); setAudit(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await endpoints.msgTemplateUpdate(sel.key, { sms, whatsapp: wa, active });
      toast.success("Template saved");
      setTemplates((ts) => ts.map((t) => (t.key === sel.key ? data : t)));
      setSel(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const reset = async () => {
    if (!window.confirm("Reset this template to the default wording?")) return;
    try {
      const { data } = await endpoints.msgTemplateReset(sel.key);
      setSms(data.sms); setWa(data.whatsapp); setActive(data.active !== false);
      setTemplates((ts) => ts.map((t) => (t.key === sel.key ? data : t)));
      setSel(data);
      toast.success("Reset to default");
    } catch { toast.error("Reset failed"); }
  };

  const loadAudit = async () => {
    try {
      const { data } = await endpoints.msgTemplateAudit(sel.key);
      setAudit(data.audit || []);
    } catch { toast.error("Failed to load history"); }
  };

  const insertVar = (setter, val, v) => setter(`${val}${val && !val.endsWith(" ") ? " " : ""}{${v}}`);

  return (
    <div className="space-y-6" data-testid="admin-messages">
      <div className="flex items-center gap-3">
        <MessageSquareText className="w-6 h-6 text-[#E11D2A]" />
        <div>
          <h1 className="text-2xl font-bold text-white">Messaging & Links</h1>
          <p className="text-sm text-slate-400">Automated message wording, customer links, and bulk messaging.</p>
        </div>
      </div>

      <div className="flex gap-1 p-1 rounded-xl bg-[#0F131A] border border-[#273042] w-fit" data-testid="messaging-tabs">
        {[["templates", "Templates", FileText], ["links", "Customer Links", Link2], ["broadcast", "Broadcast", Send]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`h-9 px-4 rounded-lg text-sm font-semibold flex items-center gap-2 transition ${tab === k ? "bg-[#E11D2A] text-white" : "text-slate-300 hover:bg-white/5"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "links" && <AdminLinks />}
      {tab === "broadcast" && <AdminBroadcast />}

      {tab === "templates" && (loading ? (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <button key={t.key} onClick={() => openEdit(t)} data-testid={`template-card-${t.key}`}
              className="text-left p-4 rounded-xl bg-[#0F131A] border border-[#273042] hover:border-[#E11D2A]/60 transition-colors">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-white">{t.title}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${t.active !== false ? "bg-green-500/15 text-green-400" : "bg-slate-500/15 text-slate-400"}`}>
                  {t.active !== false ? "Active" : "Off"}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{t.sms}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {varChips(t.required).map((v) => (
                  <span key={v} className="text-[10px] px-1.5 py-0.5 rounded bg-[#E11D2A]/10 text-[#E11D2A] font-mono">{v}</span>
                ))}
              </div>
            </button>
          ))}
        </div>
      ))}

      {sel && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setSel(null)}>
          <div className="bg-[#0B0E13] border border-[#273042] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#0B0E13] border-b border-[#273042] px-5 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white">{sel.title}</h2>
                <p className="text-xs text-slate-500">{sel.desc}</p>
              </div>
              <button onClick={() => setSel(null)} data-testid="template-close" className="p-2 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-5">
              {(sel.required?.length > 0) && (
                <div className="text-xs text-slate-400">
                  Required variables (must remain): {varChips(sel.required).map((v) => (
                    <span key={v} className="mx-1 px-1.5 py-0.5 rounded bg-[#E11D2A]/10 text-[#E11D2A] font-mono">{v}</span>
                  ))}
                </div>
              )}

              <label className="flex items-center gap-3 cursor-pointer">
                <span className="text-sm text-white">Active</span>
                <button onClick={() => setActive((a) => !a)} data-testid="template-active-toggle"
                  className={`w-11 h-6 rounded-full transition-colors ${active ? "bg-[#E11D2A]" : "bg-slate-600"} relative`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${active ? "left-[22px]" : "left-0.5"}`} />
                </button>
              </label>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-semibold text-white">SMS message</label>
                  <span className="text-xs text-slate-500">{sms.length} chars</span>
                </div>
                <textarea value={sms} onChange={(e) => setSms(e.target.value)} rows={4} data-testid="template-sms-input"
                  className="w-full rounded-xl bg-[#0A0C10] border border-[#273042] px-3 py-2 text-white text-sm outline-none focus:border-[#E11D2A]/60" />
                <div className="flex flex-wrap gap-1 mt-1">
                  {varChips(sel.required).map((v) => (
                    <button key={v} onClick={() => setSms((s) => `${s}${s && !s.endsWith(" ") ? " " : ""}${v}`)}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-300 font-mono hover:bg-white/10">+ {v}</button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-semibold text-white">WhatsApp message</label>
                  <span className="text-xs text-slate-500">{wa.length} chars</span>
                </div>
                <textarea value={wa} onChange={(e) => setWa(e.target.value)} rows={5} data-testid="template-wa-input"
                  className="w-full rounded-xl bg-[#0A0C10] border border-[#273042] px-3 py-2 text-white text-sm outline-none focus:border-[#E11D2A]/60" />
                <div className="flex flex-wrap gap-1 mt-1">
                  {varChips(sel.required).map((v) => (
                    <button key={v} onClick={() => setWa((s) => `${s}${s && !s.endsWith(" ") ? " " : ""}${v}`)}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-300 font-mono hover:bg-white/10">+ {v}</button>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <button onClick={save} disabled={saving} data-testid="template-save-btn"
                  className="h-11 px-5 rounded-full bg-[#E11D2A] text-white font-semibold flex items-center gap-2 disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                </button>
                <button onClick={reset} data-testid="template-reset-btn"
                  className="h-11 px-5 rounded-full bg-white/5 border border-[#273042] text-white flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Reset default</button>
                <button onClick={loadAudit} data-testid="template-audit-btn"
                  className="h-11 px-5 rounded-full bg-white/5 border border-[#273042] text-white flex items-center gap-2"><History className="w-4 h-4" /> History</button>
              </div>

              <p className="text-[11px] text-slate-500">Last updated {sel.updatedAt ? new Date(sel.updatedAt).toLocaleString() : "—"} by {sel.updatedBy || "—"}</p>

              {audit && (
                <div className="border-t border-[#273042] pt-4 space-y-2" data-testid="template-audit-list">
                  <h3 className="text-sm font-semibold text-white">Change history</h3>
                  {audit.length === 0 && <p className="text-xs text-slate-500">No changes recorded yet.</p>}
                  {audit.map((a, i) => (
                    <div key={i} className="text-xs text-slate-400 flex items-center gap-2">
                      <Check className="w-3 h-3 text-green-400" />
                      <span className="text-slate-300">{a.changedBy}</span> changed
                      <span className="font-mono text-[#E11D2A]">{(a.changedFields || []).join(", ")}</span>
                      <span className="text-slate-600">· {a.at ? new Date(a.at).toLocaleString() : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
